import { useHasMounted } from "@shared/ui.hooks";
import { clsx } from "clsx";
import React, { useEffect } from "react";
import { createPortal } from "react-dom";

import { ModalBody } from "./Body";
import { ModalFooter } from "./Footer";
import { ModalHeader } from "./Header";
import * as styles from "./Modal.css";
import { ModalContextProvider } from "./context";
import { ModalType } from "./types";

/**
 * A modal dialog component that appears in the center of the screen.
 * Follows WAI-ARIA guidelines for modal dialogs with keyboard navigation and screen reader support.
 *
 * @param size - Size of the modal (default, small, medium, large, full)
 * @param handleDismiss - Function to call when the modal should be closed
 * @param isOpen - Controls whether the modal is visible
 * @param ariaLabel - Accessible label for the modal (aria-label)
 * @param ariaDescription - Accessible description for the modal (aria-describedby)
 * @param id - Unique ID for the modal container
 * @param headerId - ID for the header element (used for accessibility)
 * @param dataTestId - Optional test ID for testing purposes
 * @param disableOutsideClick - Prevents closing modal when clicking outside
 * @returns {JSX.Element | null} The modal component or null if not mounted
 */
export const Modal: ModalType = ({
  children,
  size = "default",
  handleDismiss,
  isOpen,
  ariaLabel,
  ariaDescription,
  id = "modal-container",
  headerId,
  dataTestId,
  disableOutsideClick = false
}) => {
  const mounted = useHasMounted();
  const modalRef = React.useRef<HTMLDivElement>(null);
  const previousFocusRef = React.useRef<HTMLElement | null>(null);
  const [showTopBorder, setShowTopBorder] = React.useState(false);
  const [showBottomBorder, setShowBottomBorder] = React.useState(false);

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;

      setTimeout(() => {
        if (!modalRef.current) return;
        const focusableElements = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length > 0) {
          (focusableElements[0] as HTMLElement).focus();
        } else {
          modalRef.current.focus();
        }
      }, 100);
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | KeyboardEvent) => {
      if (disableOutsideClick) return;

      const target = event.target as HTMLElement;
      if (
        target !== target.closest("[role='dialog']") &&
        !target.closest("[role='dialog']") &&
        target.closest("#overlay_portal")
      ) {
        handleDismiss?.();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleDismiss?.();
        return;
      }

      if (!isOpen || event.key !== "Tab") return;

      if (modalRef.current) {
        const focusableElements = Array.from(
          modalRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        );

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[
          focusableElements.length - 1
        ] as HTMLElement;

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
      if (!disableOutsideClick) {
        document.addEventListener("click", handleClickOutside);
      }
    } else {
      window.removeEventListener("keydown", handleKeyDown);
      if (!disableOutsideClick) {
        document.removeEventListener("click", handleClickOutside);
      }
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      if (!disableOutsideClick) {
        document.removeEventListener("click", handleClickOutside);
      }
    };
  }, [isOpen, handleDismiss, disableOutsideClick]);

  if (!mounted || !isOpen) return null;

  const overlayPortal =
    typeof document !== "undefined" &&
    document.getElementById("overlay_portal");

  if (!overlayPortal) return null;

  return createPortal(
    <ModalContextProvider
      showTopBorder={showTopBorder}
      setShowTopBorder={setShowTopBorder}
      showBottomBorder={showBottomBorder}
      setShowBottomBorder={setShowBottomBorder}
    >
      <div
        data-testid={dataTestId}
        role="presentation"
        className={clsx(
          styles.overlayStyle,
          isOpen ? styles.overlayActiveStyle : styles.overlayInactiveStyle
        )}
        aria-hidden={!isOpen}
        onClick={(e) => {
          if (!disableOutsideClick && e.target === e.currentTarget) {
            handleDismiss?.();
          }
        }}
      >
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          aria-labelledby={headerId}
          aria-describedby={`${id}-description`}
          id={id}
          data-testid="modal-container"
          className={clsx(
            styles.modalStyle,
            styles.modalSizeStyle[size],
            isOpen ? styles.animationStyles.in : styles.animationStyles.out
          )}
          tabIndex={-1}
        >
          {ariaDescription && (
            <div id={`${id}-description`} className={styles.screenReaderOnly}>
              {ariaDescription}
            </div>
          )}
          {children}
        </div>
      </div>
    </ModalContextProvider>,
    overlayPortal
  );
};

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
